/* eslint-disable @typescript-eslint/no-explicit-any */
import BlogCard from '@/components/BlogList/BlogCard'
import { LoadingIndicator } from '@/components/LoadingIndicator'
import PageBreadcrumb from '@/components/PageBreadcrumb'
import { useBlogHook } from '@/hooks/useBlogHook'
import { Meta } from '@/layouts/Meta'
import { Main } from '@/templates/Main'
import type { Post, PostResponse } from '@/types'
import { setContext } from '@/utils/api-interceptors'
import {
  getBannerSSR,
  getBottomMenu,
  getConfigSSR,
  getMenus,
  getPostsSSR,
  getQuickMenu,
  getSaleRegion,
} from '@/utils/endpoint'
import { getCookies } from 'cookies-next'
import { type GetServerSideProps, type GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import InfiniteScroll from 'react-infinite-scroll-component'

type ListBlogProps = {
  postData: PostResponse
}

const ListBlog = ({ postData }: ListBlogProps) => {
  const breadcrumbs = [{ label: 'Thông tin hữu ích', href: '/post' }]
  const t = useTranslations('Common')
  const router = useRouter()

  const {
    setLoading,
    currentPage,
    setCurrentPage,
    getPosts,
    posts,
    setPosts,
    totalPages,
  } = useBlogHook(postData)

  const fetchMorePost = () => {
    setCurrentPage(currentPage + 1)
    setLoading(true)
    getPosts(
      {
        limit: 16,
        currentPage: currentPage + 1,
        sortType: 'asc',
        catDisplay: true,
        ...router.query,
      },
      {
        onSuccess: res => {
          if (posts) {
            setPosts(posts.concat(res.data.data))
          }
          setLoading(false)
        },
      }
    )
  }

  return (
    <Main
      meta={
        <Meta
          title={'Thông tin hữu ích'}
          description={'Thông tin hữu ích'}
          noFollow={false}
          noIndex={false}
          canonical={process.env.NEXT_PUBLIC_URL_WEBSITE + router.asPath}
          mobileAlternate={{
            media: 'only screen and (max-width:991px))',
            href:
              process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE + router.asPath ||
              process.env.NEXT_PUBLIC_URL_WEBSITE_MOBILE ||
              '',
          }}
        />
      }
    >
      <section className='postDetail min-h-[500px] pb-10'>
        <PageBreadcrumb
          homeText={t('home')}
          breadcrumbs={breadcrumbs}
          className='mb-[20px]'
        />
        <h1 className='mb-[30px] text-titleSection font-bold text-dark-400'>
          {t('blogListTitle')}
        </h1>
        {postData.data && postData.data.length > 0 ? (
          <InfiniteScroll
            dataLength={posts?.length || 0}
            next={fetchMorePost}
            hasMore={
              currentPage !== Number(totalPages) || Number(totalPages) === 0
            }
            loader=''
          >
            <div className='grid grid-cols-3 gap-x-[32px] gap-y-[56px]'>
              {postData.data.map((e: Post, index) => (
                <BlogCard key={index} post={e} />
              ))}
            </div>
          </InfiniteScroll>
        ) : (
          <>
            <LoadingIndicator />
          </>
        )}
      </section>
    </Main>
  )
}

export default ListBlog

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { req, res } = context
  const { token, language, messages } = await setContext(
    getCookies({ req, res }),
    {
      language: context.locale,
    }
  )

  const [
    postData,
    regions,
    menus,
    banner,
    quickMenu,
    bottomMenu,
    systemConfig,
  ] = await Promise.all([
    getPostsSSR(),
    getSaleRegion(),
    getMenus(),
    getBannerSSR(),
    getQuickMenu(),
    getBottomMenu(),
    getConfigSSR(),
  ])

  return {
    props: {
      postData,
      regions,
      menus,
      banner,
      language,
      messages,
      quickMenu,
      bottomMenu,
      systemConfig,
      token: token || null,
    },
  }
}
